'use client';

import { WarningIcon } from '@/assets/icons/WarningIcon';
import NextButton from '@/components/Button/NextButton';
import Input from '@/components/Input/Input';
import PhoneNumberInput from '@/components/Input/PhoneNumberInput';
import { ErrorOfferValues } from '@/types/error';
import { SubmitHandler, useForm } from 'react-hook-form';
import { formatSSN, unformatSSN } from '@/utils/validation';
import { fieldRules } from '@/utils/form';
import useModal from '@/hooks/useModal';
import { CreateSubmissionRequest } from '@/types/salesforce';
import { useState } from 'react';
import { SalesforceService } from '@/lib/Services/SalesforceService';
import { setSocialSecNumber } from '@/utils/calculateSalesforceStateValues';
import { ValidationErrorIcon } from '@/assets/icons/ValidationErrorIcon';
import { OfferInfoFormValues } from '@/types/modals';
import styles from './OffersInfoModal.module.scss';

interface Props {
  initialValues?: OfferInfoFormValues;
  offerInfoError?: ErrorOfferValues;
  pipedreamCallback?: (values: OfferInfoFormValues) => Promise<void>;
  submissionId?: string;
}

export default function OffersInfoModal({
  offerInfoError,
  pipedreamCallback,
  initialValues,
  submissionId,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { closeModal } = useModal();
  const defaultValues: OfferInfoFormValues = {
    ...(offerInfoError?.ssn && {
      ssn: formatSSN(initialValues?.ssn || '') ?? '',
    }),
    ...(offerInfoError?.phone && {
      phone: initialValues?.phone ?? '',
    }),
    ...(offerInfoError?.zip && { zip: initialValues?.zip ?? '' }),
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<OfferInfoFormValues>({ defaultValues });
  const isDirty = Object.keys(dirtyFields).length > 0;

  const onSubmit: SubmitHandler<OfferInfoFormValues> = async (values) => {
    setLoading(true);
    setError(false);
    try {
      const sfBody: Partial<CreateSubmissionRequest> = {};
      if (values.ssn) {
        sfBody.Social_Security_Number_SO__c = setSocialSecNumber(values.ssn);
      }
      if (values.phone) {
        sfBody.Mobile_Number_SO__c = values.phone;
      }
      if (values.zip) {
        sfBody.Zip_Code_SO__c = values.zip;
      }
      await SalesforceService.partialUpdateSubmission(
        submissionId ?? '',
        sfBody
      );
      await pipedreamCallback?.(values);
      setLoading(false);
      closeModal();
    } catch {
      setError(true);
      setLoading(false);
    }
  };

  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = unformatSSN(input.value);
    const cursorPosition = input.selectionStart || 0;
    const formattedValue = formatSSN(rawValue);
    const addedChars = formattedValue.length - input.value.length;
    e.target.value = formattedValue;
    setValue('ssn', formattedValue);
    setTimeout(() => {
      const newCursorPosition = cursorPosition + addedChars;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    });
  };

  const hasFields =
    offerInfoError?.ssn || offerInfoError?.phone || offerInfoError?.zip;

  if (!hasFields) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title}>
          <WarningIcon className={styles.icon} />
          <span>Submission information error</span>
        </div>
        <p className={styles.subtitle}>
          There is an issue with the information provided. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        <WarningIcon className={styles.icon} />
        <span>Submission information error</span>
      </div>
      <p className={styles.subtitle}>
        There is an issue with the information provided in the fields below.
        Please update to proceed to your offer wall:
      </p>

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {offerInfoError?.ssn && (
          <Input
            id="offer-info-ssn"
            label="Social security number"
            placeholder="###-##-####"
            inputMode="numeric"
            type="text"
            register={register('ssn', {
              required: fieldRules.ssn.required,
              validate: (value) =>
                value
                  ? fieldRules.ssn.validate(value)
                  : fieldRules.ssn.required,
              onChange: handleSSNChange,
            })}
            error={errors?.ssn}
          />
        )}

        {offerInfoError?.phone && (
          <PhoneNumberInput
            id="offer-info-phone"
            label="Mobile Number"
            placeholder="Your mobile number"
            error={errors?.phone}
            currentValue={watch('phone') ?? ''}
            register={register('phone', {
              required: fieldRules.phone.required,
              validate: (value) =>
                value
                  ? fieldRules.phone.validate(value)
                  : fieldRules.phone.required,
            })}
          />
        )}

        {offerInfoError?.zip && (
          <Input
            id="offer-info-zip"
            label="Zip code"
            placeholder="Your zip code"
            type="text"
            register={register('zip', {
              required: fieldRules.zip.required,
              validate: (value) =>
                value
                  ? fieldRules.zip.validate(value)
                  : fieldRules.zip.required,
              onChange: (e) => {
                const { target } = e;
                target.value = target.value.replace(/\D/g, '').slice(0, 5);
              },
            })}
            error={errors?.zip}
          />
        )}
        {error && (
          <p className={styles.error}>
            <ValidationErrorIcon className={styles.errorIcon} /> Something went
            wrong.
          </p>
        )}
        <div className={styles.buttonsWrapper}>
          <NextButton
            type="button"
            buttonType="submit"
            text="Update and continue"
            styles={styles.button}
            loading={loading}
            disabled={loading || !isDirty}
          />
        </div>
      </form>
    </div>
  );
}
